import type { LatLng } from "./types.js";
import { centroid } from "./geo.js";

export type MapEmbedOptions = {
  /** Center point; defaults to marker centroid */
  center?: LatLng;
  /** Marker locations to show */
  markers?: LatLng[];
  /** Single search query when no markers */
  query?: string;
  zoom?: number;
  /** Google Maps Embed API key (VITE_GOOGLE_MAPS_EMBED_KEY in browser) */
  apiKey?: string;
};

/**
 * Build an iframe-safe Google Maps embed URL.
 * Without an API key, falls back to the legacy `output=embed` search URL.
 */
export function buildGoogleMapsEmbedUrl(opts: MapEmbedOptions): string | null {
  const zoom = opts.zoom ?? 11;

  if (opts.markers && opts.markers.length > 0) {
    const center = opts.center ?? centroid(opts.markers);
    if (!center) return null;

    if (opts.apiKey?.trim()) {
      const markerParam = opts.markers.map((m) => `${m.lat},${m.lng}`).join("|");
      const params = new URLSearchParams({
        key: opts.apiKey.trim(),
        q: markerParam,
        center: `${center.lat},${center.lng}`,
        zoom: String(zoom),
      });
      return `https://www.google.com/maps/embed/v1/view?${params.toString()}`;
    }

    const q = encodeURIComponent(`${center.lat},${center.lng}`);
    return `https://maps.google.com/maps?q=${q}&z=${zoom}&output=embed`;
  }

  const query = opts.query?.trim();
  if (!query) return null;

  if (opts.apiKey?.trim()) {
    const params = new URLSearchParams({
      key: opts.apiKey.trim(),
      q: query,
      zoom: String(zoom),
    });
    return `https://www.google.com/maps/embed/v1/place?${params.toString()}`;
  }

  return `https://maps.google.com/maps?q=${encodeURIComponent(query)}&z=${zoom}&output=embed`;
}

/** Read embed key from Vite env when running in the browser bundle. */
export function readBrowserMapsEmbedKey(): string | undefined {
  if (typeof import.meta !== "undefined" && import.meta.env) {
    const key = import.meta.env.VITE_GOOGLE_MAPS_EMBED_KEY;
    return typeof key === "string" && key.trim() ? key.trim() : undefined;
  }
  return undefined;
}
